import IRepo from "../../interfaces/repo";
import Repo from "../../state/modules/repo";
import Guid from "../../helpers/guid";
import MappingBuilderModel from "../tracked/mappingBuilderModel";
import {ModelMappingValuesType} from "../../../formability/mappingBuilder/enums/modelMappingValuesType";
import {IModelMappingValue} from "~/interfaces/mappingInterfaces";
import ModelMapping from "~/models/mapping/modelMapping";
import {ArrayHelper} from "~/helpers/arrayHelper";
import ModelMappingValueOriginFactory from "~/models/mapping/modelMappingValueOriginFactory";
import ModelMappingValueOrigin from "~/models/mapping/modelMappingValueOrigin";
import Validatable from "~/mixins/validatable";
import ModelMappingValueValidation from "~/models/navigableValidations/modelMappingValueValidation";
import {areMappingDataTypesCompatible} from "~/models/mapping/dataTypeConcerns/mappingCompatibleDataTypes";
import ModelMappingOriginObjectModel from "~/models/mapping/modelMappingOriginObjectModel";
import {ObjectModelBuiltinPropertyType} from "~/enums/objectModelBuiltinPropertyType";
import ModelMappingValueOriginLookupField from "~/models/mapping/modelMappingValueOriginLookupField";
import modelMappingOriginForm from "~/models/mapping/modelMappingOriginForm";
import {SubmissionInformationType} from "../../../formability/mappingBuilder/enums/submission-information-type";

export default class ModelMappingValue extends Validatable(MappingBuilderModel) implements IModelMappingValue {
  type: ModelMappingValuesType;
  source_is_default_property: boolean;
  source_value_identifier: string;
  mapping_formula: string;
  static_input: string;
  destination_value_identifier: string;
  destination_is_default_property: boolean;
  sort_order: number;
  target: string;

  constructor() {
    super();
    this.defineProperty("client_id", Guid.create());
  }

  initialize(root, parent: ModelMapping = root, type, json = {}) {
    this.defineProperty("sort_order", json['sort_order'] || 0);
    this.defineProperty("static_input", "");
    this.defineProperty("target", "");
    this.defineProperty("destination_value_identifier", "");
    this.defineProperty("source_value_identifier", "");
    this.defineProperty("source_is_default_property", false);
    this.defineProperty("destination_is_default_property", false);
    this.defineProperty("mapping_formula", "");
    this.defineProperty("type", type);

    this.parent = parent;

    return this
  }

  get modelMapping(): ModelMapping {
    return this.parent;
  }

  get isLookupKey(): boolean {
    return this.modelMapping.lookup_keys.includes(this.client_id)
  }

  set isLookupKey(itIs: boolean) {
    if (itIs) {
      ArrayHelper.add(this.modelMapping.lookup_keys, this.client_id);
    } else {
      ArrayHelper.remove(this.modelMapping.lookup_keys, this.client_id);
    }
  }

  get areMappingDataTypesCompatible(): boolean {
    // Currently there is no origin for special read only values,
    // eg SubmissionInformationType, so they do not run through
    // this validator check
    if (this.type === ModelMappingValuesType.Value && this.source.origin && this.destination.origin) {
      // Alex: Aren't handling special lookup fields at the moment
      if (this.source instanceof ModelMappingValueOriginLookupField || this.destination instanceof ModelMappingValueOriginLookupField) {
        return true
      }
      return areMappingDataTypesCompatible(this.source, this.destination);
    } else {
      return true;
    }
  }

  get mappingIncludesBuiltin(): boolean {
    if (this.modelMapping.sourceOrigin instanceof ModelMappingOriginObjectModel || this.modelMapping.destinationOrigin instanceof ModelMappingOriginObjectModel) {
      return Object.values(ObjectModelBuiltinPropertyType).some( enumVal => {
        return [this.source_value_identifier, this.destination_value_identifier].includes(String(enumVal))
      });
    } else return false;
  }
  get mappingIncludesClaimRecordField(): boolean{
    return this.mappingSourceIsClaimRecordField || this.mappingDestinationIsClaimRecordField
  }
  get mappingSourceIsClaimRecordField(): boolean{
    if (this.modelMapping.sourceOrigin instanceof ModelMappingOriginObjectModel){
      return this.source_value_identifier === String(ObjectModelBuiltinPropertyType.RecordHolderEmail)
    }
    else{
      return false
    }
  }

  get mappingDestinationIsClaimRecordField(): boolean{
    if (this.modelMapping.destinationOrigin instanceof ModelMappingOriginObjectModel){
      return this.destination_value_identifier === String(ObjectModelBuiltinPropertyType.RecordHolderEmail)
    }
    else{
      return false
    }
  }
  get mappingSourceIsSubmitterEmail(): boolean {
    if (this.modelMapping.sourceOrigin instanceof modelMappingOriginForm) {
      return this.source_value_identifier === String(SubmissionInformationType.SubmittersEmail)
    }
    else{
      return false
    }
  }

  get mappingDestinationIsSubmitterEmail(): boolean {
    if (this.modelMapping.sourceOrigin instanceof modelMappingOriginForm) {
      return this.destination_value_identifier === String(SubmissionInformationType.SubmittersEmail)
    }
    else{
      return false
    }
  }
  _source: ModelMappingValueOrigin<any>
  get source(): ModelMappingValueOrigin<any> {
    if (!this._source) {
      this._source = ModelMappingValueOriginFactory.create(this.parent.source_type, this.source_value_identifier, this.modelMapping.sourceOrigin)
    }
    return this._source
  }

  _destination: ModelMappingValueOrigin<any>
  get destination(): ModelMappingValueOrigin<any> {

    const newDestination = (): ModelMappingValueOrigin => {
      return ModelMappingValueOriginFactory.create(this.parent.destination_type, this.destination_value_identifier, this.modelMapping.destinationOrigin)
    }

    const isDestinationMatched = (): boolean => {
      return !!this.destination_value_identifier && this._destination.origin?.client_id === this.destination_value_identifier
    }

    if ( !this._destination || !isDestinationMatched() ) {
      this._destination = newDestination()
    }

    return this._destination
  }

  get isLast(): boolean {
    return this.sort_order === (<ModelMappingValue>this.parent).nextSortOrderFor(this.rootName) - 1;
  }

  get isFirst(): boolean {
    return this.sort_order <= 0;
  }

  get canMoveUp(): boolean {
    return !this.isFirst
  }

  get canMoveDown(): boolean {
    return !this.isLast
  }

  moveUp(): void {
    if (!this.isFirst) {
      this.sort_order--;
      this.parent.modelMappingValueHandler.updateElementPositions(this.parent, this, this.sort_order);
    }
  }

  moveDown(): void {
    if (!this.isLast) {
      this.sort_order++;
      this.parent.modelMappingValueHandler.updateElementPositions(this.parent, this, this.sort_order);
    }
  }

  afterDestroy() {
    super.afterDestroy();
    this.parent.modelMappingValueHandler.recalculateElementPositions();
    this.parent.cleanUpLookupKeys()
  }

  get isPush(): boolean {
    return this.target === this.destination_value_identifier
  }

  get directionDisplay(): string {
    if (this.isPush) {
      return "pushes to"
    } else {
      return "pulls from"
    }
  }

  // 'origin' as in 'ModelMappingValueOrigin'
  get readOrigin(): ModelMappingValueOrigin {
    return this.isPush ? this.source : this.destination;
  }

  get writeOrigin(): ModelMappingValueOrigin {
    return this.isPush ? this.destination : this.source;
  }

  // Class Configurations

  except = ['mapping', 'createEmbeddedObject'];

  static repo: IRepo<ModelMappingValue> = new Repo<ModelMappingValue>('modelMappingValues', ModelMappingValue, {});
  repo: IRepo<this>;

  addToRepo(): void {
    ModelMappingValue.repo.add(this);
  }

  static get displayName(): string {
    return 'ModelMappingValue';
  }

  get collectionKey(): string {
    return "modelMappingValues";
  }

  get UNTRACKED_PROPERTIES(): string[] {
    return [
      'u_at',
    ];
  }

  get rootName(): string {
    return 'model_mapping_values';
  }

  static get rootName(): string {
    return 'model_mapping_values';
  }

  // ******************** VALIDATABLE******************** //
  _validation: ModelMappingValueValidation<ModelMappingValue> = new ModelMappingValueValidation(this);

  getDisplayName(propName: string, value?): string {
    let displayNames = {
      "type": "Mapping Value Type",
      "source_value_identifier": "Value Source Field",
      "mapping_formula": "Value Source Formula",
      "static_input": "Value Source Static Value",
      "destination_value_identifier": "Value Destination Field",
    }
    return displayNames[propName];
  }
  // ******************** VALIDATABLE END******************** //

}